import Overlay from 'components/Layout/Overlay';
import {FC, ReactNode, useRef} from 'react';
import {
    useModalButtons,
    useModalKeyEscape,
    useModalMouseEscape,
} from 'utils/modal';

import ModalBody from './ModalBody';
import ModalButton from './ModalButton';
import ModalContent from './ModalContent';
import ModalFooter from './ModalFooter';
import ModalHeader from './ModalHeader';
import {usePreventBackgroundScroll} from './utils';

export type ModalProps = {
    children: ReactNode;
    className?: string;
    isOpen: boolean;
    onClose: () => void;
    preventOutsideClick?: boolean;
};

type ModalComponentProps = {
    Body: typeof ModalBody;
    Button: typeof ModalButton;
    Footer: typeof ModalFooter;
    Header: typeof ModalHeader;
} & FC<ModalProps>;

const Modal: ModalComponentProps = ({
    children,
    className,
    isOpen,
    onClose,
    preventOutsideClick,
}) => {
    usePreventBackgroundScroll(isOpen);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const {hasCancel, onCheckButtons, submitCount} = useModalButtons();
    useModalKeyEscape({hasCancel, isOpen, onClose, submitCount});
    useModalMouseEscape({enabled: !preventOutsideClick, isOpen, onClose});

    if (!isOpen) {
        return null;
    }

    return (
        <Overlay
            className="modal-overlay"
            layoutLayer="z-modal"
            layoutMethod="modal"
        >
            <ModalContent
                className={className}
                onCheckButtons={onCheckButtons}
                ref={containerRef}
            >
                {children}
            </ModalContent>
        </Overlay>
    );
};

Modal.Body = ModalBody;
Modal.Button = ModalButton;
Modal.Footer = ModalFooter;
Modal.Header = ModalHeader;

export default Modal;
